import React, { useEffect, useRef } from 'react';
import {
  CircleInfoIcon,
  CircleCheckIcon,
  TriangleExclamationIcon,
  XmarkIcon,
} from '@revfluence/fresh-icons/solid';
import { Alert } from '@frontend/shadcn/components/ui/alert';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Spinner } from '../../../Analytics/components/ui/spinner';

export interface BulkOperationState {
  isVisible: boolean;
  state: 'loading' | 'success' | 'warning' | 'error';
  operationType: 'invite-to-campaign' | 'add-to-group' | 'add-to-campaign';
  count: number;
  targetTitle: string; // Campaign or Group name
  successCount?: number;
  errorCount?: number;
  errorMessage?: string;
}

interface BulkOperationAlertProps {
  operationState: BulkOperationState;
  onDismiss: () => void;
}

const getOperationText = (operationType: BulkOperationState['operationType']) => {
  switch (operationType) {
    case 'invite-to-campaign':
      return 'Inviting';
    case 'add-to-group':
      return 'Adding';
    case 'add-to-campaign':
      return 'Adding';
    default:
      return 'Processing';
  }
};

const getOperationTarget = (operationType: BulkOperationState['operationType']) => {
  switch (operationType) {
    case 'invite-to-campaign':
      return 'campaign';
    case 'add-to-group':
      return 'group';
    case 'add-to-campaign':
      return 'campaign';
    default:
      return 'target';
  }
};

const alertVariants = {
  loading: {
    className: 'bg-blue-1 border-blue-3',
    icon: <CircleInfoIcon className="!text-blue-5" />,
    iconColor: 'text-blue-6',
  },
  success: {
    className: 'bg-green-100 border-green-300',
    icon: <CircleCheckIcon className="!text-green-500" />,
    iconColor: 'text-green-600',
  },
  warning: {
    className: 'bg-yellow-100 border-yellow-300',
    icon: <TriangleExclamationIcon className="!text-yellow-500" />,
    iconColor: 'text-yellow-600',
  },
  error: {
    className: 'bg-red-100 border-red-300',
    icon: <TriangleExclamationIcon className="!text-red-500" />,
    iconColor: 'text-red-600',
  },
};

interface DismissButtonProps {
  onDismiss: () => void;
}

const DismissButton: React.FC<DismissButtonProps> = ({ onDismiss }) => (
  <Button
    variant="ghost"
    size="sm"
    onClick={onDismiss}
    className="p-1 h-auto hover:bg-transparent"
    aria-label="Dismiss notification"
  >
    <XmarkIcon className="w-4 h-4 text-gray-500" />
  </Button>
);

interface LoadingContentProps {
  operationType: BulkOperationState['operationType'];
  count: number;
  targetTitle: string;
}

const LoadingContent: React.FC<LoadingContentProps> = ({ operationType, count, targetTitle }) => {
  const operationText = getOperationText(operationType);

  return (
    <div className="flex items-center gap-3">
      <Spinner size="sm" spinning />
      <span className="text-blue-6 font-medium">
        {operationText} {count} creator{count !== 1 ? 's' : ''} to {targetTitle}
      </span>
    </div>
  );
};

interface SuccessContentProps {
  operationType: BulkOperationState['operationType'];
  successCount: number;
  targetTitle: string;
  variant: typeof alertVariants.success;
  onDismiss: () => void;
}

const SuccessContent: React.FC<SuccessContentProps> = ({
  operationType,
  successCount,
  targetTitle,
  variant,
  onDismiss
}) => {
  const operationTarget = getOperationTarget(operationType);

  return (
    <div className="flex items-center justify-between w-full">
      <div className="flex items-center gap-3">
        {variant.icon}
        <span className={`font-medium ${variant.iconColor}`}>
          Successfully {operationType === 'invite-to-campaign' ? 'invited' : 'added'} {successCount} creator{successCount !== 1 ? 's' : ''} to {operationTarget} "{targetTitle}"
        </span>
      </div>
      <DismissButton onDismiss={onDismiss} />
    </div>
  );
};

interface WarningContentProps {
  operationType: BulkOperationState['operationType'];
  successCount: number;
  errorCount: number;
  count: number;
  targetTitle: string;
  variant: typeof alertVariants.warning;
  onDismiss: () => void;
}

const WarningContent: React.FC<WarningContentProps> = ({
  operationType,
  successCount,
  errorCount,
  count,
  targetTitle,
  variant,
  onDismiss
}) => (
  <div className="flex items-center justify-between w-full">
    <div className="flex items-center gap-3">
      {variant.icon}
      <div className={`${variant.iconColor}`}>
        <div className="font-medium">
          Partially completed: {successCount} of {count} creator{count !== 1 ? 's' : ''}
        </div>
        <div className="text-sm">
          {successCount} successfully {operationType === 'invite-to-campaign' ? 'invited' : 'added'} to "{targetTitle}". {errorCount} failed.
        </div>
      </div>
    </div>
    <DismissButton onDismiss={onDismiss} />
  </div>
);

interface ErrorContentProps {
  operationType: BulkOperationState['operationType'];
  count: number;
  targetTitle: string;
  errorMessage?: string;
  variant: typeof alertVariants.error;
  onDismiss: () => void;
}

const ErrorContent: React.FC<ErrorContentProps> = ({
  operationType,
  count,
  targetTitle,
  errorMessage,
  variant,
  onDismiss
}) => {
  const operationTarget = getOperationTarget(operationType);

  return (
    <div className="flex items-center justify-between w-full">
      <div className="flex items-center gap-3">
        {variant.icon}
        <div className={`${variant.iconColor}`}>
          <div className="font-medium">
            Failed to {operationType === 'invite-to-campaign' ? 'invite' : 'add'} creators to {operationTarget}
          </div>
          <div className="text-sm">
            {errorMessage || `Unable to ${operationType === 'invite-to-campaign' ? 'invite' : 'add'} ${count} creator${count !== 1 ? 's' : ''} to "${targetTitle}". Please try again.`}
          </div>
        </div>
      </div>
      <DismissButton onDismiss={onDismiss} />
    </div>
  );
};

export const BulkOperationAlert: React.FC<BulkOperationAlertProps> = ({
  operationState,
  onDismiss,
}) => {
  const autoHideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    isVisible,
    state,
    operationType,
    count,
    targetTitle,
    successCount = 0,
    errorCount = 0,
    errorMessage,
  } = operationState;

  // Auto-hide after success (5 seconds)
  useEffect(() => {
    if (state === 'success' && isVisible) {
      const timer = setTimeout(() => {
        onDismiss();
      }, 5000);
      autoHideTimerRef.current = timer;
    }

    return () => {
      if (autoHideTimerRef.current) {
        clearTimeout(autoHideTimerRef.current);
        autoHideTimerRef.current = null;
      }
    };
  }, [state, isVisible, onDismiss]);

  if (!isVisible) return null;

  const variant = alertVariants[state];

  return (
    <div className="mx-4 mb-4">
      <Alert
        className={`${variant.className} rounded-md`}
        role="status"
        aria-live="polite"
        aria-label={`Bulk operation ${state}`}
      >
        <div
          className="flex items-center gap-2 w-full"
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '8px',
            alignSelf: 'stretch',
            borderRadius: '6px',
            background: state === 'loading' ? 'var(--Action-Blue-blue-1, #E6F7FF)' : undefined,
          }}
        >
          {state === 'loading' && (
            <LoadingContent
              operationType={operationType}
              count={count}
              targetTitle={targetTitle}
            />
          )}

          {state === 'success' && (
            <SuccessContent
              operationType={operationType}
              successCount={successCount}
              targetTitle={targetTitle}
              variant={variant}
              onDismiss={onDismiss}
            />
          )}

          {state === 'warning' && (
            <WarningContent
              operationType={operationType}
              successCount={successCount}
              errorCount={errorCount}
              count={count}
              targetTitle={targetTitle}
              variant={variant}
              onDismiss={onDismiss}
            />
          )}

          {state === 'error' && (
            <ErrorContent
              operationType={operationType}
              count={count}
              targetTitle={targetTitle}
              errorMessage={errorMessage}
              variant={variant}
              onDismiss={onDismiss}
            />
          )}
        </div>
      </Alert>
    </div>
  );
};
